/**
 * useCrmConversationDeal — estado reativo do deal CRM vinculado a uma conversa.
 *
 * F4: dois seams no core OSS leem o MESMO estado — o bloco Kanban no ContactPanel
 * (Seam A) e o botão "Concluir venda"/"Marcar perdido" no ResolveAction (Seam B).
 * Para evitar duplo-fetch e divergência, o cache é MODULE-SCOPED: uma entrada
 * singleton por conversationId, compartilhada entre todas as instâncias do composable.
 *
 * Contrato do envelope `by_conversation` (alinhado com o backend):
 *   { deal, stages, agents, action_types }.
 *   - deal:   serialized | null. null = conversa sem deal (inbox interna id=6, pré-CRM).
 *   - stages: etapas NÃO-terminais do funil do deal, ordenadas por posicao asc.
 *   - agents: candidatos a responsável — [{ id, name, thumbnail? }].
 *   - action_types (F1): tipos de "Próxima Ação" permitidos p/ (deal.trilha, deal.stage),
 *                  [{ value, label }] — recalculado a cada refresh (muda com etapa/trilha).
 *
 * Invalidação: refresh() refaz o fetch. applyDeal() aplica um deal já reserializado
 * (resposta de move/update/win/lose) sem ida extra ao servidor — mantém os seams em sincronia.
 *
 * Reatividade: o estado por conversationId vive num Map module-scoped de refs. Cada chamada
 * do composable resolve (ou cria) a entrada do conversationId passado e devolve as MESMAS refs.
 */
import { ref, computed, watch, toValue } from 'vue';
import crmBoardApi from 'dashboard/api/cw_custom/crmBoard.js';

// Map<conversationId, entry> — singleton enquanto o módulo estiver carregado.
// entry = { deal, stages, agents, loading, loaded, accountId }.
const cache = new Map();

function ensureEntry(conversationId) {
  if (!cache.has(conversationId)) {
    cache.set(conversationId, {
      deal: ref(null),
      stages: ref([]),
      agents: ref([]),
      // F1 §4: taxonomia de tipos de "Próxima Ação" permitidos para
      // (deal.trilha, deal.stage). Recalculada a cada refresh — [{ value, label }].
      actionTypes: ref([]),
      loading: ref(false),
      loaded: ref(false),
      // Race guard: descarta respostas de refresh() anteriores quando elas chegam fora de ordem.
      reqId: 0,
    });
  }
  return cache.get(conversationId);
}

/**
 * @param {import('vue').MaybeRefOrGetter<number|string>} accountId
 * @param {import('vue').MaybeRefOrGetter<number|string>} conversationId
 */
export function useCrmConversationDeal(accountId, conversationId) {
  const currentId = computed(() => toValue(conversationId));

  // Resolve a entrada reativa para o conversationId atual. Trocar de conversa
  // aponta as computeds para outra entrada do cache (sem vazar estado entre conversas).
  const entry = computed(() => ensureEntry(currentId.value));

  const deal = computed(() => entry.value.deal.value);
  const stages = computed(() => entry.value.stages.value);
  const agents = computed(() => entry.value.agents.value);
  const actionTypes = computed(() => entry.value.actionTypes.value);
  const loading = computed(() => entry.value.loading.value);

  const isCrmLinked = computed(() => !!deal.value);
  const isOpen = computed(() => deal.value?.resultado === 'aberto');

  async function refresh() {
    const id = currentId.value;
    if (id == null || id === '') return;
    const e = ensureEntry(id);
    e.reqId += 1;
    const thisReq = e.reqId;
    e.loading.value = true;
    try {
      const res = await crmBoardApi.fetchDealByConversation(
        toValue(accountId),
        id
      );
      if (thisReq !== e.reqId) return; // resposta stale — outra conversa/refresh assumiu
      const data = res.data ?? {};
      e.deal.value = data.deal ?? null;
      e.stages.value = data.stages ?? [];
      e.agents.value = data.agents ?? [];
      e.actionTypes.value = data.action_types ?? [];
      e.loaded.value = true;
    } catch {
      if (thisReq !== e.reqId) return;
      // Falha de fetch não derruba a conversa: mantém o bloco escondido (deal=null).
      // Numa REVALIDAÇÃO (já carregou antes) preservamos o último bom estado — o
      // stale-while-revalidate da A3 não pode transformar um blip de rede em
      // "esta conversa não tem deal". Só o primeiro load zera.
      if (!e.loaded.value) {
        e.deal.value = null;
        e.stages.value = [];
        e.agents.value = [];
        e.actionTypes.value = [];
        e.loaded.value = true;
      }
    } finally {
      if (thisReq === e.reqId) e.loading.value = false;
    }
  }

  /**
   * Aplica um deal já reserializado (resposta de move/update/win/lose) ao cache,
   * mantendo stages/agents intactos. Evita um fetch redundante e sincroniza os 2 seams.
   * @param {Object|null} nextDeal
   */
  function applyDeal(nextDeal) {
    const e = ensureEntry(currentId.value);
    e.deal.value = nextDeal ?? null;
  }

  // Auto-refresh ao montar/trocar de conversa. immediate cobre o primeiro mount;
  // o watch cobre a navegação entre conversas.
  //
  // STALE-WHILE-REVALIDATE (A3 da auditoria 2026-08-03): antes revalidávamos só
  // quando `!loaded` — mas o board (CrmBoard/CrmColumn) muta o deal pelo seu
  // próprio caminho e NUNCA escreve neste cache. Mover/ganhar/perder um deal pelo
  // board e voltar pra conversa mostrava o estado antigo até um F5. Agora sempre
  // disparamos o fetch; o valor cacheado continua renderizado enquanto a
  // revalidação está em voo (zero flicker), e o race-guard de `refresh()`
  // descarta respostas fora de ordem. Nenhum dos 3 seams renderiza a partir de
  // `loading`, então revalidar não pisca skeleton; o guard `!loading` continua
  // colapsando os 3 mounts simultâneos num único GET (`refresh` marca loading
  // sincronamente, antes do primeiro await).
  watch(
    currentId,
    id => {
      if (id == null || id === '') return;
      const e = ensureEntry(id);
      if (!e.loading.value) refresh();
    },
    { immediate: true }
  );

  return {
    deal,
    stages,
    agents,
    actionTypes,
    isCrmLinked,
    isOpen,
    loading,
    refresh,
    applyDeal,
  };
}

// Exposto só para os testes — reseta o cache module-scoped entre casos.
export function resetCrmConversationDealCache() {
  cache.clear();
}
